import React, { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import LoginScreen from './components/LoginScreen';
import Header from './components/Header';
import BottomNav from './components/BottomNav';
import GameModal from './components/GameModal';
import ExportModal from './components/ExportModal';
import GameDetailsModal from './components/GameDetailsModal';
import ConfirmDialog from './components/ConfirmDialog';
import { HomeView, LibraryView, StatsView, CollectionsPanel } from './components/AppContent';
import { useGameSearch } from './hooks/useGameSearch';
import { useAuth } from './hooks/useAuth';
import { addGame, updateGame, deleteGame, softDeleteGame, restoreGame, getGames, logout } from './services/firebase';
import { detectPlatform, mapGenresToTags, calculateStats, filterGames, activeGames, trashedGames, pushStatusHistory } from './utils/gameUtils';
import { getLists, createList, deleteList, toggleGameInList, getQueue, addToQueue, removeFromQueue, moveInQueue, MAX_QUEUE_SIZE } from './utils/localLibrary';
import { useToast } from './context/ToastContext';

const TRASH_RETENTION_DAYS = 30;

const JaJoguei = () => {
  const { user, loading: authLoading } = useAuth();
  const toast = useToast();
  const [games, setGames] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('ja-joguei-view-mode') || 'grid');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('ja-joguei-sort') || 'recent');
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('home');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [permanentDeleteTarget, setPermanentDeleteTarget] = useState(null);
  const [lists, setLists] = useState([]);
  const [queue, setQueue] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    platform: 'PC',
    rating: 5,
    status: 'completed',
    dateFinished: new Date().toISOString().split('T')[0],
    platinado: false,
    notes: '',
    tags: [],
    coverImage: ''
  });

  const {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    showResults,
    setShowResults
  } = useGameSearch();

  // Carregar jogos do Firebase quando usuário logar
  useEffect(() => {
    const loadGames = async () => {
      if (user) {
        setLoading(true);
        try {
          const userGames = await getGames(user.uid);
          const cutoff = Date.now() - TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
          const expired = userGames.filter(g => g.deletedAt && new Date(g.deletedAt).getTime() < cutoff);
          expired.forEach(g => deleteGame(user.uid, g.id).catch(() => {}));
          const expiredIds = new Set(expired.map(g => g.id));
          setGames(userGames.filter(g => !expiredIds.has(g.id)));
          setLists(getLists(user.uid));
          setQueue(getQueue(user.uid));
        } catch (error) {
          console.error('Erro ao carregar jogos:', error);
          toast.error('Erro ao carregar seus jogos.');
        } finally {
          setLoading(false);
        }
      } else {
        setGames([]);
        setLists([]);
        setQueue([]);
      }
    };

    loadGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const saved = localStorage.getItem('ja-joguei-library-filters');
    if (saved) {
      try {
        const filters = JSON.parse(saved);
        setFilterStatus(filters.status || 'all');
        setFilterPlatform(filters.platform || 'all');
      } catch (error) {
        localStorage.removeItem('ja-joguei-library-filters');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('ja-joguei-library-filters', JSON.stringify({ status: filterStatus, platform: filterPlatform }));
    localStorage.setItem('ja-joguei-sort', sortOrder);
    localStorage.setItem('ja-joguei-view-mode', viewMode);
  }, [filterStatus, filterPlatform, sortOrder, viewMode]);

  const handleLogin = () => {
    // O useAuth já gerencia o estado do usuário
  };

  const handleLogout = async () => {
    try {
      await logout();
      setGames([]);
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      toast.error('Não foi possível sair da conta.');
    }
  };

  const handleSelectGameFromAPI = (game) => {
    const detectedPlatform = detectPlatform(game.platforms);
    const apiTags = mapGenresToTags(game.genres);

    const coverImage = game.background_image ||
                      (game.short_screenshots && game.short_screenshots[0]?.image) ||
                      '';

    setFormData({
      ...formData,
      name: game.name,
      coverImage,
      platform: detectedPlatform,
      tags: [...new Set([...apiTags])].slice(0, 5),
      rating: game.rating ? Math.round(game.rating) : 5
    });

    setSearchQuery(game.name);
    setShowResults(false);
  };

  const handleAddGame = async () => {
    if (!formData.name.trim()) {
      toast.warning('Preencha o nome do jogo.');
      return;
    }

    setLoading(true);
    try {
      const newGame = await addGame(user.uid, formData);
      setGames([newGame, ...games]);
      setShowAddModal(false);
      resetForm();
      toast.success(`${newGame.name} adicionado à sua biblioteca.`);
    } catch (error) {
      console.error('Erro ao adicionar jogo:', error);
      toast.error('Erro ao adicionar jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditGame = async () => {
    if (!formData.name.trim()) {
      toast.warning('Preencha o nome do jogo.');
      return;
    }

    setLoading(true);
    try {
      const history = pushStatusHistory(editingGame, formData.status);
      const payload = { ...formData, statusHistory: history };
      await updateGame(user.uid, editingGame.id, payload);
      const updatedGames = games.map(g =>
        g.id === editingGame.id ? { ...g, ...payload, id: g.id } : g
      );
      setGames(updatedGames);
      setEditingGame(null);
      resetForm();
      toast.success('Alterações salvas.');
    } catch (error) {
      console.error('Erro ao atualizar jogo:', error);
      toast.error('Erro ao atualizar jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const patchGame = (id, patch) => setGames(prev => prev.map(g => (g.id === id ? { ...g, ...patch } : g)));

  const handleDeleteGame = (id) => {
    setDeleteTarget(games.find(game => game.id === id) || null);
  };

  const confirmDeleteGame = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setLoading(true);
    try {
      const deletedAt = await softDeleteGame(user.uid, target.id);
      patchGame(target.id, { deletedAt });
      setDeleteTarget(null);
      if (selectedGameId === target.id) setSelectedGameId(null);
      toast('Jogo movido para a lixeira.', {
        type: 'info',
        action: { label: 'Desfazer', onClick: () => handleRestoreGame(target.id) }
      });
    } catch (error) {
      console.error('Erro ao mover jogo para a lixeira:', error);
      toast.error('Erro ao excluir jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreGame = async (id) => {
    try {
      await restoreGame(user.uid, id);
      patchGame(id, { deletedAt: null });
      toast.success('Jogo restaurado.');
    } catch (error) {
      console.error('Erro ao restaurar jogo:', error);
      toast.error('Não foi possível restaurar o jogo.');
    }
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeleteTarget) return;
    const target = permanentDeleteTarget;
    setLoading(true);
    try {
      await deleteGame(user.uid, target.id);
      setGames(prev => prev.filter(g => g.id !== target.id));
      setPermanentDeleteTarget(null);
      toast.success('Jogo excluído definitivamente.');
    } catch (error) {
      console.error('Erro ao excluir jogo definitivamente:', error);
      toast.error('Erro ao excluir jogo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const quickChangeStatus = async (game, newStatus, { silent = false } = {}) => {
    const previousStatus = game.status;
    if (previousStatus === newStatus) return;
    const history = pushStatusHistory(game, newStatus);
    const patch = { status: newStatus, statusHistory: history };
    if (newStatus === 'completed' && !game.dateFinished) {
      patch.dateFinished = new Date().toISOString().split('T')[0];
    }
    try {
      await updateGame(user.uid, game.id, patch);
      patchGame(game.id, patch);
      if (!silent) {
        toast(`Status atualizado para "${patch.status === 'completed' ? 'Completo' : newStatus}".`, {
          type: 'success',
          action: { label: 'Desfazer', onClick: () => quickChangeStatus({ ...game, ...patch }, previousStatus, { silent: true }) }
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast.error('Não foi possível atualizar o status.');
    }
  };

  const toggleFavorite = async (game) => {
    const favorite = !game.favorite;
    try {
      await updateGame(user.uid, game.id, { favorite });
      patchGame(game.id, { favorite });
    } catch (error) {
      console.error('Erro ao favoritar jogo:', error);
      toast.error('Não foi possível atualizar os favoritos.');
    }
  };

  const toggleArchive = async (game) => {
    const archived = !game.archived;
    try {
      await updateGame(user.uid, game.id, { archived });
      patchGame(game.id, { archived });
      toast(archived ? 'Jogo arquivado.' : 'Jogo desarquivado.', {
        type: 'info',
        action: { label: 'Desfazer', onClick: () => toggleArchive({ ...game, archived }) }
      });
    } catch (error) {
      console.error('Erro ao arquivar jogo:', error);
      toast.error('Não foi possível arquivar o jogo.');
    }
  };

  const addJournalEntry = async (game, text) => {
    const journal = [...(game.journal || []), { date: new Date().toISOString(), text }];
    try {
      await updateGame(user.uid, game.id, { journal });
      patchGame(game.id, { journal });
    } catch (error) {
      console.error('Erro ao salvar anotação:', error);
      toast.error('Não foi possível salvar a anotação.');
    }
  };

  const registerCompletion = async (game) => {
    const today = new Date().toISOString().split('T')[0];
    const completions = [...(game.completions || []), { date: new Date().toISOString() }];
    const history = pushStatusHistory(game, 'completed');
    const patch = { completions, status: 'completed', dateFinished: today, statusHistory: history };
    try {
      await updateGame(user.uid, game.id, patch);
      patchGame(game.id, patch);
      toast.success('Nova finalização registrada!');
    } catch (error) {
      console.error('Erro ao registrar rejogada:', error);
      toast.error('Não foi possível registrar a finalização.');
    }
  };

  const toggleQueue = (game) => {
    if (!user) return;
    const inQueue = queue.includes(game.id);
    if (!inQueue && queue.length >= MAX_QUEUE_SIZE) {
      toast.warning('A fila "Jogar em seguida" já tem 5 jogos.');
      return;
    }
    const updated = inQueue ? removeFromQueue(user.uid, game.id) : addToQueue(user.uid, game.id);
    setQueue(updated);
  };

  const moveQueueItem = (gameId, direction) => {
    if (!user) return;
    setQueue(moveInQueue(user.uid, gameId, direction));
  };

  const createListHandler = (name) => {
    if (!user) return;
    setLists(createList(user.uid, name));
  };

  const deleteListHandler = (listId) => {
    if (!user) return;
    setLists(deleteList(user.uid, listId));
  };

  const toggleListHandler = (listId, gameId) => {
    if (!user) return;
    setLists(toggleGameInList(user.uid, listId, gameId));
  };

  const startEdit = (game) => {
    setEditingGame(game);
    setFormData(game);
    setSearchQuery(game.name);
    setSelectedGameId(null);
  };

  const openDetails = (game) => setSelectedGameId(game.id);

  const editFromDetails = (game) => {
    setSelectedGameId(null);
    startEdit(game);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      platform: 'PC',
      rating: 5,
      status: 'completed',
      dateFinished: new Date().toISOString().split('T')[0],
      platinado: false,
      notes: '',
      tags: [],
      coverImage: ''
    });
    setSearchQuery('');
    setShowResults(false);
  };

  const toggleTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const library = useMemo(() => activeGames(games), [games]);
  const visibleGames = useMemo(() => library.filter(g => !g.archived), [library]);
  const trash = useMemo(() => trashedGames(games), [games]);
  const selectedGame = games.find(g => g.id === selectedGameId) || null;

  const filteredGamesList = [...filterGames(library, { searchTerm, status: filterStatus, platform: filterPlatform, showArchived })].sort((a, b) => {
    if (sortOrder === 'name') return a.name.localeCompare(b.name, 'pt-BR');
    if (sortOrder === 'rating') return (b.rating || 0) - (a.rating || 0);
    if (sortOrder === 'oldest') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
  const stats = calculateStats(library);
  const queuedGames = queue.map(id => library.find(g => g.id === id)).filter(Boolean);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white text-lg">Carregando...</div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen">
      <Header
        user={user}
        onLogout={handleLogout}
        onExport={() => setShowExportModal(true)}
        onAdd={() => setShowAddModal(true)}
        activeView={activeView}
        onNavigate={setActiveView}
      />

      {activeView === 'home' && (
        <HomeView
          user={user}
          games={visibleGames}
          stats={stats}
          loading={loading}
          onAdd={() => setShowAddModal(true)}
          onLibrary={() => setActiveView('library')}
          onOpen={openDetails}
        />
      )}

      {activeView === 'library' && (
        <LibraryView
          games={library}
          filteredGames={filteredGamesList}
          loading={loading && !showAddModal && !editingGame}
          onOpen={openDetails}
          onDelete={handleDeleteGame}
          onAdd={() => setShowAddModal(true)}
          onToggleFavorite={toggleFavorite}
          viewMode={viewMode}
          onChangeViewMode={setViewMode}
          trashCount={trash.length}
          onOpenTrash={() => setShowTrash(true)}
          onOpenCollections={() => setShowCollections(true)}
          queuedGames={queuedGames}
          onMoveQueueItem={moveQueueItem}
          onRemoveFromQueue={(id) => toggleQueue({ id })}
          onOpenQueueGame={openDetails}
          filters={{
            searchTerm,
            onSearchChange: setSearchTerm,
            filterStatus,
            onStatusChange: setFilterStatus,
            filterPlatform,
            onPlatformChange: setFilterPlatform,
            sortOrder,
            onSortChange: setSortOrder,
            showArchived,
            onToggleArchived: setShowArchived
          }}
        />
      )}

      {activeView === 'stats' && (
        <StatsView stats={stats} games={library} onExport={() => setShowExportModal(true)} onOpen={openDetails} />
      )}

      <BottomNav
        user={user}
        activeView={activeView}
        onNavigate={setActiveView}
        onAdd={() => setShowAddModal(true)}
        onExport={() => setShowExportModal(true)}
        onLogout={handleLogout}
      />

      {(showAddModal || editingGame) && (
        <GameModal
          isEditing={!!editingGame}
          editingGameId={editingGame?.id || null}
          games={library}
          formData={formData}
          searchQuery={searchQuery}
          searchResults={searchResults}
          isSearching={isSearching}
          showSearchResults={showResults}
          onSearchChange={setSearchQuery}
          onSelectGame={handleSelectGameFromAPI}
          onFormChange={setFormData}
          onToggleTag={toggleTag}
          onSave={editingGame ? handleEditGame : handleAddGame}
          onCancel={() => {
            setShowAddModal(false);
            setEditingGame(null);
            resetForm();
          }}
        />
      )}

      {selectedGame && (
        <GameDetailsModal
          game={selectedGame}
          onClose={() => setSelectedGameId(null)}
          onEdit={editFromDetails}
          onQuickStatus={quickChangeStatus}
          onToggleFavorite={toggleFavorite}
          onToggleArchive={toggleArchive}
          onAddJournalEntry={addJournalEntry}
          onRegisterCompletion={registerCompletion}
          onToggleQueue={toggleQueue}
          isQueued={queue.includes(selectedGame.id)}
          queueFull={queue.length >= MAX_QUEUE_SIZE}
          lists={lists}
          onToggleList={toggleListHandler}
          onCreateList={createListHandler}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Excluir este jogo?"
          description={<>O jogo <b className="text-slate-200">{deleteTarget.name}</b> vai para a lixeira e pode ser restaurado por {TRASH_RETENTION_DAYS} dias.</>}
          confirmLabel={loading ? 'Excluindo...' : 'Excluir jogo'}
          danger
          busy={loading}
          onConfirm={confirmDeleteGame}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {permanentDeleteTarget && (
        <ConfirmDialog
          title="Excluir definitivamente?"
          description={<>Essa ação não pode ser desfeita. <b className="text-slate-200">{permanentDeleteTarget.name}</b> será apagado para sempre.</>}
          confirmLabel={loading ? 'Excluindo...' : 'Excluir para sempre'}
          danger
          busy={loading}
          onConfirm={confirmPermanentDelete}
          onCancel={() => setPermanentDeleteTarget(null)}
        />
      )}

      {showCollections && (
        <CollectionsPanel
          lists={lists}
          games={library}
          onDeleteList={deleteListHandler}
          onClose={() => setShowCollections(false)}
          onOpenGame={(game) => { setShowCollections(false); openDetails(game); }}
        />
      )}

      {showTrash && (
        <TrashPanel
          games={trash}
          retentionDays={TRASH_RETENTION_DAYS}
          onClose={() => setShowTrash(false)}
          onRestore={handleRestoreGame}
          onPermanentDelete={setPermanentDeleteTarget}
        />
      )}

      {showExportModal && (
        <ExportModal
          games={library}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

const TrashPanel = ({ games, retentionDays, onClose, onRestore, onPermanentDelete }) => (
  <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-end sm:items-center sm:justify-center sm:p-5" role="dialog" aria-modal="true" aria-label="Lixeira">
    <button className="absolute inset-0 w-full" onClick={onClose} aria-label="Fechar lixeira" />
    <div className="relative w-full sm:max-w-lg max-h-[85vh] overflow-y-auto surface-raised rounded-b-none sm:rounded-b-2xl p-5 sm:p-6 animate-slideUp">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-extrabold">Lixeira</h2>
        <button onClick={onClose} className="icon-button" aria-label="Fechar"><Plus className="w-4 h-4 rotate-45" /></button>
      </div>
      <p className="text-xs text-slate-500 mb-5">Jogos excluídos ficam aqui por {retentionDays} dias antes de serem apagados para sempre.</p>
      {games.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">A lixeira está vazia.</p>
      ) : (
        <div className="space-y-2">
          {games.map(game => (
            <div key={game.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-white/[0.07] bg-white/[0.025]">
              <div className="min-w-0 flex-1">
                <div className="font-bold text-sm truncate">{game.name}</div>
                <div className="text-[10px] text-slate-600">Excluído em {new Date(game.deletedAt).toLocaleDateString('pt-BR')}</div>
              </div>
              <button onClick={() => onRestore(game.id)} className="btn-secondary px-3 py-2 text-[11px] shrink-0">Restaurar</button>
              <button onClick={() => onPermanentDelete(game)} className="btn-danger px-3 py-2 text-[11px] shrink-0">Excluir</button>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

export default JaJoguei;
